"use client";

import { installMediaFocus, onMediaFocus } from "@/lib/media-focus";
import { autoplayBasis, buildAutoplayQueue } from "@/lib/autoplay-queue";
import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from "react";
import { trackMediaEvent } from "@/services/stats";

// Supported audio formats for web playback
export const PLAYABLE_AUDIO_FORMATS = [
  "audio/mpeg",       // mp3
  "audio/mp4",        // m4a, mp4 audio
  "audio/ogg",        // ogg
  "audio/wav",        // wav
  "audio/webm",       // webm audio
  "audio/flac",       // flac (Chrome, Edge, Firefox)
  "audio/aac",        // aac
  "audio/x-m4a",      // m4a alternate
  "audio/aiff",       // aiff (Safari)
  "audio/x-aiff",     // aiff alternate
];

export const PLAYABLE_VIDEO_FORMATS = [
  "video/mp4",
  "video/webm",
  "video/ogg",
];

export const SUPPORTED_EXTENSIONS = {
  audio: ["mp3", "m4a", "ogg", "wav", "webm", "flac", "aac", "aiff", "aif"],
  video: ["mp4", "webm", "ogv"],
};

export type MediaType = "audio" | "video" | "youtube" | "radio";
export type RepeatMode = "off" | "all" | "one";

export interface MediaTrack {
  id: string;
  title: string;
  artist?: string;
  album?: string;
  duration?: number; // seconds
  imageUrl?: string;
  /** Multiple artwork images for slideshow (release images[] + artist images[]) */
  images?: string[];
  /** Streaming URL — M4A/AAC 256kbps (browser-compatible, faststart) */
  audioUrl?: string;
  /** Hi-res lossless URL — WAV 16-bit/44.1kHz for quality listeners */
  wavUrl?: string;
  videoUrl?: string;
  youtubeId?: string;
  mediaType: MediaType;
  genre?: string;
  // For credits/lyrics
  credits?: { role: string; name: string; href?: string; avatarUrl?: string }[];
  lyrics?: string;
  description?: string;
  /** Release year e.g. "2021" */
  year?: string;
  /** Label name */
  label?: string;
  /** Producer names */
  producers?: string[];
  // For linking
  artistSlug?: string;
  albumSlug?: string;
  trackSlug?: string;
  /** File format being played: 'WAV', 'M4A', 'MP3', 'FLAC' etc. */
  fileFormat?: string;
  /** Additional artists featured on the track — shown as "feat. X, Y" */
  featuredArtists?: string[];
  /** Radio station this track was loaded from — used to show "now playing" on the radio listing page */
  radioStationId?: string;
  /** How it was made — provenance label (human / ai-assisted / ai). Optional;
   *  the player also fetches it from the work doc when absent. */
  aiLevel?: string;
  aiPercent?: number;
  /** Explicit content flag (parental advisory). */
  explicit?: boolean;
}

export interface MediaPlayerState {
  // Current track
  currentTrack: MediaTrack | null;
  // Queue
  queue: MediaTrack[];
  queueIndex: number;
  /** Set while the queue is autoplay after a finished queue — the Queue tab
   *  says "Autoplay · more like <basedOn>" instead of "Playing from <album>". */
  queueContext: { kind: 'autoplay'; basedOn: string } | null;
  // Playback state
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  // Error state (e.g. unsupported format like AIF in Chrome)
  audioError: string | null;
  /**
   * Stream quality preference:
   *  'auto'      — M4A on mobile/slow connections, WAV on fast desktop (default)
   *  'streaming' — Always use M4A (256kbps AAC)
   *  'lossless'  — Prefer WAV if available; auto-fallback to M4A on stall
   */
  streamQuality: 'auto' | 'streaming' | 'lossless';
  // Modes
  shuffle: boolean;
  repeat: RepeatMode;
  // UI
  isActive: boolean;
  isExpanded: boolean;
  // Web Audio API for visualization
  analyserL: AnalyserNode | null;
  analyserR: AnalyserNode | null;
  /** True when any audio is flowing through the analyser (main player OR external preview) */
  isVisualizerActive: boolean;
  /** True when an external YouTube iframe (video page, hero) is playing — drives VU simulation */
  isExternalYT: boolean;
  /** True when the BB DJ mixer is active and piping audio through the VU chain */
  isDJMode: boolean;
  /** Track currently playing on the active DJ deck — shown in the global player bar */
  djTrackInfo: { title: string; artist?: string } | null;
}

/** Firestore stores artist/album as `{ name }` / `{ title }` objects as often
 *  as plain strings; one such object rendered as text crashed the whole page
 *  (React #31, found 2026-10-31 via coπ voice playback). Every track that
 *  enters the queue goes through this. */
const asText = (v: unknown): string | undefined =>
  v == null ? undefined : typeof v === "object" ? String((v as any).name ?? (v as any).title ?? "") : String(v);
function cleanTrack(t: MediaTrack): MediaTrack {
  if (typeof t.title !== "object" && typeof t.artist !== "object" && typeof t.album !== "object") return t;
  return { ...t, title: asText(t.title) || "", artist: asText(t.artist), album: asText(t.album) };
}

export interface MediaPlayerActions {
  // Playback
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  setVolume: (vol: number) => void;
  toggleMute: () => void;
  // Queue management
  playTrack: (track: MediaTrack) => void;
  playTracks: (tracks: MediaTrack[], startIndex?: number) => void;
  /**
   * Load a queue into the player WITHOUT starting playback — the player bar
   * appears paused on the first track, one tap from playing. Used for the
   * visitor welcome ("a random track loaded in the player ready to go").
   */
  cueTracks: (tracks: MediaTrack[], startIndex?: number) => void;
  addToQueue: (track: MediaTrack) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  skipNext: () => void;
  skipPrev: () => void;
  // Stream quality
  setStreamQuality: (q: 'auto' | 'streaming' | 'lossless') => void;
  // Modes
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  // UI
  setActive: (active: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  // Utility
  formatTime: (seconds: number) => string;
  /**
   * Register a function that returns tracks to auto-play when the queue is
   * exhausted. Pass null to unregister. Used by MediaPlayer to inject
   * same-genre tracks from Firestore.
   */
  setAutoNextProvider: (fn: (() => Promise<MediaTrack[]>) | null) => void;
  /**
   * Connect an external HTMLAudioElement (e.g. mastering preview) to the
   * Web Audio analyser chain so VU meters reflect that audio source.
   * Returns a cleanup function that disconnects the source.
   */
  connectExternalAudio: (el: HTMLAudioElement) => () => void;
  /**
   * Connect a live MediaStream (microphone, screen capture, etc.) to the
   * Web Audio analyser chain so VU meters show mic/call levels.
   * Returns a cleanup function that disconnects the source and restores the
   * previous analyser state.
   */
  connectMicStream: (stream: MediaStream) => () => void;
  /**
   * Signal that an external YouTube-iframe source (video page, hero video) started playing.
   * The VU strip will run the YouTube simulation until deactivateExternalVisualizer() is called.
   */
  activateExternalVisualizer: () => void;
  /** Signal that the external iframe source stopped — VU returns to silence or main player. */
  deactivateExternalVisualizer: () => void;
  /**
   * Called by the DJ mixer to announce the active deck track.
   * Sets isDJMode + djTrackInfo and activates the visualizer.
   * Call with active=false (or on unmount) to clear the indicator.
   */
  setDJMode: (active: boolean, info?: { title: string; artist?: string } | null) => void;
}

type MediaPlayerContextType = MediaPlayerState & MediaPlayerActions;

// ─── iOS envelope analyser ───────────────────────────────────────────────────
// iOS can't route the <audio> element through Web Audio without killing
// lock-screen playback (see setupAudioContext), and Gordon rejected the fake
// clock-pulse meters. The hybrid: decode the track ONCE off the playback
// path, precompute its REAL spectrum into a 20fps × 64-band envelope, and
// let EnvelopeAnalyser impersonate an AnalyserNode answering from that
// envelope at audio.currentTime. Meters/artwork follow the actual music,
// audio stays native, the lock screen keeps playing.

const IOS_ENV_FPS = 20;
const IOS_ENV_BANDS = 64;
const IOS_ENV_MAX_SEC = 660; // analysis cap (11 min) — bounds memory + CPU

const IS_IOS = typeof navigator !== "undefined" && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1));

function iosFft(re: Float32Array, im: Float32Array) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { let t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let curR = 1, curI = 0;
      for (let k = 0; k < len / 2; k++) {
        const uR = re[i + k], uI = im[i + k];
        const vR = re[i + k + len / 2] * curR - im[i + k + len / 2] * curI;
        const vI = re[i + k + len / 2] * curI + im[i + k + len / 2] * curR;
        re[i + k] = uR + vR; im[i + k] = uI + vI;
        re[i + k + len / 2] = uR - vR; im[i + k + len / 2] = uI - vI;
        const nR = curR * wr - curI * wi; curI = curR * wi + curI * wr; curR = nR;
      }
    }
  }
}

interface IOSEnvelope { url: string; fps: number; frames: Uint8Array[]; levels: Float32Array; sampleRate: number; }

/** Precompute the 64-band envelope. Yields to the event loop every ~12
 *  analysed seconds so a long track never janks the main thread. */
async function buildEnvelopeFromBuffer(buf: AudioBuffer, url: string): Promise<IOSEnvelope> {
  const sr = buf.sampleRate;
  const secs = Math.min(buf.duration, IOS_ENV_MAX_SEC);
  const totalFrames = Math.max(1, Math.floor(secs * IOS_ENV_FPS));
  const win = 2048;
  const chL = buf.getChannelData(0);
  const chR = buf.numberOfChannels > 1 ? buf.getChannelData(1) : chL;
  const hann = new Float32Array(win);
  for (let i = 0; i < win; i++) hann[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (win - 1)));
  const nyq = sr / 2, fMin = 30;
  const bandBin = new Int32Array(IOS_ENV_BANDS + 1);
  for (let b = 0; b <= IOS_ENV_BANDS; b++) {
    const f = fMin * Math.pow(nyq / fMin, b / IOS_ENV_BANDS);
    bandBin[b] = Math.max(1, Math.min(win / 2 - 1, Math.round((f / nyq) * (win / 2))));
  }
  const frames: Uint8Array[] = new Array(totalFrames);
  const levels = new Float32Array(totalFrames);
  const re = new Float32Array(win), im = new Float32Array(win);
  for (let fIdx = 0; fIdx < totalFrames; fIdx++) {
    if (fIdx > 0 && fIdx % 240 === 0) await new Promise(r => setTimeout(r, 0));
    const start = Math.min(Math.max(0, chL.length - win), Math.floor((fIdx / IOS_ENV_FPS) * sr));
    let rms = 0;
    for (let i = 0; i < win; i++) {
      const s = 0.5 * (chL[start + i] + chR[start + i]);
      re[i] = s * hann[i]; im[i] = 0;
      rms += s * s;
    }
    rms = Math.sqrt(rms / win);
    levels[fIdx] = Math.min(1, rms * 3.5);
    iosFft(re, im);
    const frame = new Uint8Array(IOS_ENV_BANDS);
    for (let b = 0; b < IOS_ENV_BANDS; b++) {
      let sum = 0, n = 0;
      for (let k = bandBin[b]; k <= bandBin[b + 1]; k++) { sum += Math.hypot(re[k], im[k]); n++; }
      const mag = n ? sum / n : 0;
      // ≈ AnalyserNode's byte scale: window-normalised dB in [-100,-30] → 0..255
      const db = 20 * Math.log10((2 * mag) / (win * 0.375) + 1e-9);
      frame[b] = Math.max(0, Math.min(255, Math.round(((db + 100) / 70) * 255)));
    }
    frames[fIdx] = frame;
  }
  return { url, fps: IOS_ENV_FPS, frames, levels, sampleRate: sr };
}

/** Impersonates the AnalyserNode surface the visual consumers use (byte/float
 *  frequency + time-domain reads, fftSize, smoothing, min/max dB, context). */
class EnvelopeAnalyser {
  fftSize = 2048;
  smoothingTimeConstant = 0.85;
  minDecibels = -100;
  maxDecibels = -30;
  context: { sampleRate: number } = { sampleRate: 44100 };
  private smooth: Float32Array | null = null;
  private phase: number;
  constructor(
    private readFrame: () => { frame: Uint8Array | null; level: number },
    phase = 0,
  ) { this.phase = phase; }
  get frequencyBinCount() { return this.fftSize / 2; }
  private bandFor(i: number, n: number): number {
    const nyq = this.context.sampleRate / 2, fMin = 30;
    const f = Math.max(fMin, (i / n) * nyq);
    const t = Math.log(f / fMin) / Math.log(nyq / fMin);
    return Math.max(0, Math.min(IOS_ENV_BANDS - 1, Math.floor(t * IOS_ENV_BANDS)));
  }
  getByteFrequencyData(arr: Uint8Array) {
    const { frame } = this.readFrame();
    const n = arr.length;
    if (!this.smooth || this.smooth.length !== n) this.smooth = new Float32Array(n);
    const s = Math.max(0, Math.min(0.95, this.smoothingTimeConstant));
    for (let i = 0; i < n; i++) {
      const v = frame ? frame[this.bandFor(i, n)] : 0;
      this.smooth[i] = s * this.smooth[i] + (1 - s) * v;
      arr[i] = this.smooth[i] | 0;
    }
  }
  getFloatFrequencyData(arr: Float32Array) {
    const { frame } = this.readFrame();
    for (let i = 0; i < arr.length; i++) {
      const v = frame ? frame[this.bandFor(i, arr.length)] : 0;
      arr[i] = this.minDecibels + (v / 255) * (this.maxDecibels - this.minDecibels);
    }
  }
  getByteTimeDomainData(arr: Uint8Array) {
    const { level } = this.readFrame();
    for (let i = 0; i < arr.length; i++) {
      const t = (i / arr.length) * Math.PI * 2;
      const w = Math.sin(t * 3 + this.phase) * 0.6 + Math.sin(t * 7.3 + this.phase * 1.61) * 0.4;
      arr[i] = 128 + Math.round(w * 110 * level);
    }
    this.phase += 0.13;
  }
  getFloatTimeDomainData(arr: Float32Array) {
    const { level } = this.readFrame();
    for (let i = 0; i < arr.length; i++) {
      const t = (i / arr.length) * Math.PI * 2;
      arr[i] = (Math.sin(t * 3 + this.phase) * 0.6 + Math.sin(t * 7.3 + this.phase * 1.61) * 0.4) * level;
    }
    this.phase += 0.13;
  }
  connect() { /* not part of a real graph */ }
  disconnect() { /* not part of a real graph */ }
}

const MediaPlayerContext = createContext<MediaPlayerContextType | null>(null);

export function useMediaPlayer() {
  const ctx = useContext(MediaPlayerContext);
  if (!ctx) throw new Error("useMediaPlayer must be used within MediaPlayerProvider");
  return ctx;
}

export function MediaPlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Refs to avoid stale closures in audio event handlers
  const queueRef = useRef<MediaTrack[]>([]);
  const queueIndexRef = useRef(0);
  // Saved in/out markers for the CURRENT track (from the Media Vault audio
  // editor). Playback starts at `in` and auto-advances at `out`.
  const trackBoundsRef = useRef<{ in: number; out: number }>({ in: 0, out: 0 });
  const boundsTokenRef = useRef(0);
  const shuffleRef = useRef(false);
  const repeatRef = useRef<RepeatMode>("off");

  // ── localStorage persistence key ──────────────────────────────────────────
  const PLAYER_STORAGE_KEY = 'bb_player_state';
  const readPlayerStorage = () => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = localStorage.getItem(PLAYER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  // State — lazy initializers read from localStorage so values survive refresh
  const [currentTrack, setCurrentTrack] = useState<MediaTrack | null>(null);
  const currentTrackRef = useRef<MediaTrack | null>(null);
  const [queue, setQueue] = useState<MediaTrack[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // These five start at SSR-safe constants (matching the server) rather than
  // reading localStorage in their initializer — the player UI that displays
  // them is always gated behind `isActive` (false until the restore effect
  // below runs, post-mount), so this wasn't reachable during hydration, but
  // keeping the same defer-to-post-mount pattern used everywhere else in the
  // app avoids it ever becoming one if that gating changes.
  // 100% by default (Gordon 2026-11-01): the device's own volume does the work;
  // BB's slider is for balancing against other apps. A saved choice wins.
  const [volume, setVolumeState] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [shuffle, setShuffle] = useState<boolean>(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');
  const [isActive, setIsActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [streamQuality, setStreamQuality] = useState<'auto' | 'streaming' | 'lossless'>('auto');
  useEffect(() => {
    const s = readPlayerStorage();
    if (typeof s?.volume === 'number') setVolumeState(s.volume);
    if (s?.isMuted === true) setIsMuted(true);
    if (s?.shuffle === true) setShuffle(true);
    if (s?.repeat === 'all' || s?.repeat === 'one') setRepeat(s.repeat);
    try {
      const saved = localStorage.getItem('bb_stream_quality');
      if (saved === 'streaming' || saved === 'lossless' || saved === 'auto') setStreamQuality(saved);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const streamQualityRef = useRef<'auto' | 'streaming' | 'lossless'>('auto');
  const handleSetStreamQuality = useCallback((q: 'auto' | 'streaming' | 'lossless') => {
    streamQualityRef.current = q;
    setStreamQuality(q);
    // Persist to localStorage so the choice survives reload
    try { localStorage.setItem('bb_stream_quality', q); } catch { /* ignore */ }
  }, []);

  // Web Audio API for real-time analysis
  const audioCtxRef = useRef<AudioContext | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  // Tracks whether Web Audio setup was attempted — prevents creating leaked AudioContexts
  // on every play call when setup will always fail (e.g., no CORS on the bucket).
  const audioCtxAttemptedRef = useRef(false);
  const [analyserL, setAnalyserL] = useState<AnalyserNode | null>(null);
  const [analyserR, setAnalyserR] = useState<AnalyserNode | null>(null);
  // Mirror refs so connectExternalAudio can read current analyser state inside
  // a useCallback with [] deps (avoids stale closure capturing initial null).
  const analyserLRef = useRef<AnalyserNode | null>(null);
  const analyserRRef = useRef<AnalyserNode | null>(null);
  const [isVisualizerActive, setIsVisualizerActive] = useState(false);
  // Elements registered via connectExternalAudio (Media Vault trim editor's
  // preview/scrub players, DJ decks, etc.) — tracked so the footer player and
  // any external player can each stop the other instead of both playing at
  // once (Gordon 2026-09-21: "you can not play the media vault player to
  // check you trims ... and media player in footer at the same time ...
  // either or" — the two were fully uncoordinated before this, leaving the
  // browser to silently arbitrate which one actually made sound).
  const externalAudioElsRef = useRef<Set<HTMLAudioElement>>(new Set());
  // Set by external sources (YouTube iframe, hero video) that can't connect via Web Audio
  const [isExternalYT, setIsExternalYT] = useState(false);
  // DJ mixer state — set by dj-mixer.tsx when a deck is playing
  const [isDJMode, setIsDJMode] = useState(false);
  const [djTrackInfo, setDjTrackInfo] = useState<{ title: string; artist?: string } | null>(null);

  // Auto-next provider: injected by MediaPlayer to fetch same-genre tracks
  const autoNextFnRef = useRef<(() => Promise<MediaTrack[]>) | null>(null);
  // Autoplay context for the Queue tab, and the queue it continued from so
  // Previous on its first track goes back there.
  const [queueContext, setQueueContextState] = useState<MediaPlayerState['queueContext']>(null);
  const queueContextRef = useRef<MediaPlayerState['queueContext']>(null);
  const autoplayFromRef = useRef<{ queue: MediaTrack[]; index: number; context: MediaPlayerState['queueContext'] } | null>(null);
  const setQueueContext = useCallback((c: MediaPlayerState['queueContext']) => {
    queueContextRef.current = c;
    setQueueContextState(c);
  }, []);
  /** A queue the person chose replaces any autoplay context. */
  const resetQueueContext = useCallback(() => {
    autoplayFromRef.current = null;
    setQueueContext(null);
  }, [setQueueContext]);
  /** Counts consecutive auto-skip errors to detect all-unplayable queues. */
  const consecutiveSkipsRef = useRef(0);
  /** One in-place recovery attempt per source URL for transient decode/network errors. */
  const decodeRetryRef = useRef<string | null>(null);
  const setAutoNextProvider = useCallback((fn: (() => Promise<MediaTrack[]>) | null) => {
    autoNextFnRef.current = fn;
  }, []);

  /** Route Firebase Storage audio through our server-side proxy so the browser
   *  receives CORS headers. This allows crossOrigin='anonymous' on the <audio> element
  /**
   * Decide which audio URL to load based on quality preference + estimated
   * network speed (Network Information API where available).
   *
   * Priority:
   *  'lossless' + wavUrl available → WAV  (with stall → M4A fallback)
   *  'auto' + desktop + 4g/wifi    → WAV if available, else M4A
   *  everything else               → M4A (audioUrl)
   */
  const resolveAudioUrl = useCallback((track: MediaTrack): string => {
    const m4a = track.audioUrl || '';
    const wav = track.wavUrl || '';
    if (!m4a) return wav;   // no streaming copy → WAV is all we have
    if (!wav) return m4a;   // no lossless copy → always M4A

    // Only serve raw WAV when the user EXPLICITLY chooses lossless. Browsers
    // decode WAV unreliably — 24-bit / non-PCM / odd-header WAVs throw
    // MEDIA_ERR_DECODE and surface as "Audio file appears to be corrupted"
    // (Gordon: same song plays in one browser but is 'corrupted' in another —
    // that browser was on 'auto' and got the WAV). 'auto' + 'streaming' both use
    // the transparent M4A, which plays everywhere. WAV stays for downloads + the
    // opt-in lossless toggle (which also falls back to M4A on decode error below).
    const quality = streamQualityRef.current;
    if (quality === 'lossless') return wav;
    return m4a;
  }, []);

  // ── iOS envelope state (see EnvelopeAnalyser above) ──────────────────────
  const iosEnvelopeRef = useRef<IOSEnvelope | null>(null);
  const iosEnvUrlRef = useRef('');
  const readIOSFrame = useCallback(() => {
    const env = iosEnvelopeRef.current;
    const el = audioRef.current;
    if (!env || !el || el.paused || el.ended) return { frame: null as Uint8Array | null, level: 0 };
    const idx = Math.max(0, Math.min(env.frames.length - 1, Math.floor(el.currentTime * env.fps)));
    return { frame: env.frames[idx], level: env.levels[idx] };
  }, []);
  const ensureIOSEnvelope = useCallback(async (src: string) => {
    if (!IS_IOS || iosEnvUrlRef.current === src) return;
    iosEnvUrlRef.current = src;
    try {
      const res = await fetch(src);
      if (!res.ok) return;
      const ab = await res.arrayBuffer();
      if (iosEnvUrlRef.current !== src) return; // track changed mid-download
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      const dctx = new AC();
      const buf: AudioBuffer = await new Promise((res2, rej) => dctx.decodeAudioData(ab, res2, rej));
      try { dctx.close(); } catch { /* ignore */ }
      if (iosEnvUrlRef.current !== src) return;
      const env = await buildEnvelopeFromBuffer(buf, src);
      if (iosEnvUrlRef.current !== src) return;
      iosEnvelopeRef.current = env;
      // Hand consumers the impersonators once — stable identities so their
      // effects don't re-run per track.
      if (!analyserLRef.current) {
        const l = new EnvelopeAnalyser(readIOSFrame, 0) as unknown as AnalyserNode;
        const r = new EnvelopeAnalyser(readIOSFrame, 1.3) as unknown as AnalyserNode;
        (l as unknown as EnvelopeAnalyser).context.sampleRate = env.sampleRate;
        (r as unknown as EnvelopeAnalyser).context.sampleRate = env.sampleRate;
        analyserLRef.current = l; analyserRRef.current = r;
        setAnalyserL(l); setAnalyserR(r);
      }
    } catch { /* meters keep their dead-signal fallback — audio unaffected */ }
  }, [readIOSFrame]);

  /**
   * Proxy Firebase Storage URLs through the audio-proxy Next.js route
   *  which in turn enables the Web Audio API analyser chain (VU meters, waveform decode).
   *  Non-Firebase URLs are used as-is. */
  const loadAudio = useCallback((url: string) => {
    if (!audioRef.current) return;
    setAudioError(null); // clear any previous error when loading a new track
    // Proxy ALL remote audio through our CORS-enabled audio-proxy route.
    // This is required because createMediaElementSource (Web Audio API for VU
    // meters + waveform) silences cross-origin audio that was fetched without CORS.
    const isRemote = url.startsWith('http://') || url.startsWith('https://');
    const isLocalApi = url.startsWith('/api/');
    const src = (isRemote && !isLocalApi) ? `/api/audio-proxy?url=${encodeURIComponent(url)}` : url;
    audioRef.current.crossOrigin = 'anonymous';
    audioRef.current.src = src;
    // iOS: kick off the off-path envelope analysis so the meters follow the
    // REAL music without rerouting playback (lock-screen stays alive).
    if (IS_IOS) void ensureIOSEnvelope(src);
    // If Web Audio hasn't been wired up yet (no source node), allow another attempt
    // now that we may have a CORS-enabled URL.
    if (!mediaSourceRef.current) audioCtxAttemptedRef.current = false;
  }, [ensureIOSEnvelope]);

  /** Resume the AudioContext (if suspended) then start the audio element playing.
   *  Must be called from a user-gesture stack. */
  const resumeAndPlay = useCallback(async () => {
    if (!audioRef.current) return;
    // Stop any externally-registered audio (Media Vault trim/preview
    // players, DJ decks, etc.) so starting the footer player always wins
    // outright instead of silently fighting for the same output — see
    // connectExternalAudio, which does the reverse when an external element
    // starts.
    externalAudioElsRef.current.forEach(el => { try { el.pause(); } catch { /* ignore */ } });
    if (audioCtxRef.current?.state === 'suspended') {
      try { await audioCtxRef.current.resume(); } catch { /* non-fatal */ }
    }
    await audioRef.current.play().catch(console.error);
  }, []);

  const setupAudioContext = useCallback(() => {
    // Guard: already successfully set up — reuse the existing graph across all tracks.
    if (mediaSourceRef.current) return;
    // Guard: already attempted in this tick — prevents rapid duplicate calls.
    if (audioCtxAttemptedRef.current) return;
    if (!audioRef.current || typeof window === "undefined") return;
    // iOS NOTE — the HYBRID (Gordon 2026-09-11: "app stops playing in iphone
    // but not android when screen saver activates"): routing the <audio>
    // element through createMediaElementSource sends its sound through the
    // AudioContext, and iOS suspends that context on lock/background and
    // REFUSES programmatic resume until a foreground gesture — the
    // onstatechange auto-resume below is silently ignored there, so music
    // died the moment the screen locked. We tried the real analyser on iOS
    // (for honest VU reactivity) and this was the predicted regression.
    // On iOS the element now plays NATIVELY (lock-screen safe, Media Session
    // supplies the lock-screen controls) and the VU/artwork layers use their
    // synthetic-reactivity fallback. Every other platform keeps the real
    // analyser chain.
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
    if (isIOS) {
      audioCtxAttemptedRef.current = true; // never re-attempt — never reroute on iOS
      return;
    }
    audioCtxAttemptedRef.current = true;
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass({ latencyHint: 'playback' });
      const source = ctx.createMediaElementSource(audioRef.current);
      const splitter = ctx.createChannelSplitter(2);
      const nodeL = ctx.createAnalyser();
      const nodeR = ctx.createAnalyser();
      const merger = ctx.createChannelMerger(2);

      nodeL.fftSize = 2048;
      nodeR.fftSize = 2048;
      nodeL.smoothingTimeConstant = 0.85;
      nodeR.smoothingTimeConstant = 0.85;

      source.connect(splitter);
      splitter.connect(nodeL, 0);
      splitter.connect(nodeR, 1);
      nodeL.connect(merger, 0, 0);
      nodeR.connect(merger, 0, 1);
      merger.connect(ctx.destination);

      // The element's audio now routes THROUGH the context, so if the browser
      // suspends it (screen off / screensaver / backgrounded) the sound stops.
      // statechange fires immediately (unlike the throttled watchdog timer that
      // never runs while the screen is asleep) — resume the moment it suspends
      // so music keeps playing with the screen black.
      ctx.onstatechange = () => {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      };

      audioCtxRef.current = ctx;
      mediaSourceRef.current = source;
      setAnalyserL(nodeL);
      setAnalyserR(nodeR);
    } catch (e) {
      console.warn("Web Audio Context setup failed — will retry on next play:", e);
      // Allow retry: reset the attempt flag so the next user gesture can try again.
      audioCtxAttemptedRef.current = false;
    }
  }, []);

  // Sync isVisualizerActive with main player state.
  // Also resume the AudioContext when playback starts — browsers suspend it
  // after a period of inactivity, which causes the VU strip to go dark.
  useEffect(() => {
    if (isPlaying) {
      setIsVisualizerActive(true);
      // Resume suspended AudioContext so VU meters stay alive after track changes
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
    }
    // Don't set false here — external sources manage their own active state
  }, [isPlaying]);

  // VU watchdog: while visualizer is supposed to be active, poll every 2 s and
  // resume any browser-suspended AudioContext. Fixes the "VU goes dark mid-session"
  // bug caused by Chrome/Safari suspending contexts after ~30 s of background tab time.
  useEffect(() => {
    if (!isVisualizerActive) return;
    const id = setInterval(() => {
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
    }, 2000);
    return () => clearInterval(id);
  }, [isVisualizerActive]);

  // Keep refs in sync with state
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { queueIndexRef.current = queueIndex; }, [queueIndex]);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);
  useEffect(() => { analyserLRef.current = analyserL; }, [analyserL]);
  // Feed the live audio analyser into the shared avatar reactivity source so
  // every GenerativeArtistAvatar on screen breathes / pulses with the track.
  useEffect(() => {
    let cancelled = false;
    // Dynamic import keeps the player bundle thin on builds that don't render
    // avatars at all — the hook lives in its own chunk.
    import("@/hooks/use-avatar-reactivity").then(m => {
      if (cancelled) return;
      m.setAvatarAnalyser(analyserL);
    }).catch(() => { /* hook not present — no-op */ });
    return () => { cancelled = true; };
  }, [analyserL]);
  useEffect(() => { analyserRRef.current = analyserR; }, [analyserR]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);

  // Initialize audio element
  useEffect(() => {
    if (typeof window !== "undefined" && !audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "metadata";
      audioRef.current.volume = volume;
      // crossOrigin is set dynamically per-track in loadAudio() below.
      // Publish the element globally so the VU strip can read its LIVE
      // playback state directly. The element is created via `new Audio()` and
      // never attached to the DOM, so the strip's `getElementsByTagName('audio')`
      // scan found nothing — on iOS (no Web Audio analyser) that left the meter
      // frozen because it had no ground-truth "is audio playing" signal.
      (window as unknown as { __bbAudioEl?: HTMLAudioElement }).__bbAudioEl = audioRef.current;
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
      }
    };
  }, []);

  // ── Restore player state from localStorage on mount ──────────────────────
  // Runs once after audio element exists. Loads the last track/queue so the
  // player bar reappears in paused state — user must click play to resume.
  useEffect(() => {
    const s = readPlayerStorage();
    if (!s?.currentTrack) return;
    const restoredTrack: MediaTrack = cleanTrack(s.currentTrack);
    const restoredQueue: MediaTrack[] = Array.isArray(s.queue) && s.queue.length > 0 ? s.queue.filter(Boolean).map(cleanTrack) : [restoredTrack];
    const restoredIndex: number = typeof s.queueIndex === 'number' ? s.queueIndex : 0;
    const restoredTime: number = typeof s.currentTime === 'number' ? s.currentTime : 0;

    setCurrentTrack(restoredTrack);
    currentTrackRef.current = restoredTrack;
    setQueue(restoredQueue);
    queueRef.current = restoredQueue;
    setQueueIndex(restoredIndex);
    queueIndexRef.current = restoredIndex;
    setIsActive(true);

    // Load audio src + seek without playing (respects browser autoplay policy)
    if (audioRef.current && restoredTrack.audioUrl && restoredTrack.mediaType === 'audio') {
      const url = restoredTrack.audioUrl;
      const isRemote = url.startsWith('http://') || url.startsWith('https://');
      const src = isRemote ? `/api/audio-proxy?url=${encodeURIComponent(url)}` : url;
      audioRef.current.crossOrigin = 'anonymous';
      audioRef.current.src = src;
      audioRef.current.load();
      if (restoredTime > 0) {
        const onLoadedMetadata = () => {
          if (audioRef.current) audioRef.current.currentTime = restoredTime;
          setCurrentTime(restoredTime);
        };
        audioRef.current.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Persist player state to localStorage on key state changes ────────────
  useEffect(() => {
    if (!currentTrack) return;
    try {
      const existing = readPlayerStorage();
      localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify({
        ...existing,
        currentTrack,
        queue,
        queueIndex,
        volume,
        isMuted,
        shuffle,
        repeat,
        // currentTime is updated separately in the timeupdate handler
      }));
    } catch { /* quota exceeded — ignore */ }
  }, [currentTrack, queue, queueIndex, volume, isMuted, shuffle, repeat]);

  // Use refs inside callbacks to avoid stale closures in audio event handlers
  const loadAndPlayIndex = useCallback((index: number) => {
    const q = queueRef.current;
    if (index < 0 || index >= q.length) return;
    const track = q[index];
    setQueueIndex(index);
    queueIndexRef.current = index;
    setCurrentTrack(track);
    setIsActive(true);

    // ── In/Out markers (saved in the Media Vault audio editor) ─────────────
    // Playback starts at inPoint and auto-advances at outPoint. Bounds come
    // from the track object when the caller passed them, else from the work
    // doc — so EVERY play path honours saved markers without each call site
    // needing to know about them.
    trackBoundsRef.current = {
      in: Math.max(0, Number((track as any).inPoint) || 0),
      out: Math.max(0, Number((track as any).outPoint) || 0),
    };
    const boundsToken = ++boundsTokenRef.current;
    if (!(track as any).inPoint && !(track as any).outPoint && track.id) {
      import('firebase/firestore').then(async (fs) => {
        try {
          const { db } = await import('@/lib/firebase');
          const snap = await fs.getDoc(fs.doc(db, 'works', track.id));
          if (boundsToken !== boundsTokenRef.current || !snap.exists()) return;
          const d = snap.data() as any;
          const inP = Math.max(0, Number(d.inPoint) || 0);
          const outP = Math.max(0, Number(d.outPoint) || 0);
          trackBoundsRef.current = { in: inP, out: outP };
          // Doc arrived after playback started — jump to the in point if we're
          // still at the very beginning (never yank the user mid-listen).
          const el = audioRef.current;
          if (el && inP > 0 && el.currentTime < Math.min(1.5, inP)) {
            try { el.currentTime = inP; } catch { /* not seekable yet */ }
          }
        } catch { /* bounds stay as-is */ }
      }).catch(() => {});
    }

    // For YouTube/video tracks without audioUrl, just set state (iframe handles playback)
    if (track.youtubeId || track.mediaType === "youtube") {
      // Stop audio element if it was playing something else
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
      }
      setIsPlaying(true);
      return;
    }

    const resolvedUrl = resolveAudioUrl(track);
    if (audioRef.current && resolvedUrl) {
      loadAudio(resolvedUrl);
      audioRef.current.load();
      // Start at the saved in point (already known from track props; the doc
      // fetch above also seeks if it resolves after metadata).
      if (trackBoundsRef.current.in > 0) {
        const el = audioRef.current;
        const seekIn = () => {
          if (trackBoundsRef.current.in > 0 && el.currentTime < trackBoundsRef.current.in) {
            try { el.currentTime = trackBoundsRef.current.in; } catch { /* ignore */ }
          }
        };
        el.addEventListener('loadedmetadata', seekIn, { once: true });
      }
      setupAudioContext();
      resumeAndPlay();

      // Stall detection: if WAV is buffering too slowly, fall back to M4A
      if (resolvedUrl === track.wavUrl && track.audioUrl && track.audioUrl !== resolvedUrl) {
        const el = audioRef.current;
        const fallbackTimer = setTimeout(() => {
          if (!audioRef.current) return;
          const stuck = audioRef.current.readyState < 3 || audioRef.current.buffered.length === 0;
          if (stuck) {
            console.warn('[bb-player] WAV stalling — falling back to M4A');
            loadAudio(track.audioUrl!);
            audioRef.current.load();
            resumeAndPlay();
          }
        }, 8000);
        el.addEventListener('canplaythrough', () => clearTimeout(fallbackTimer), { once: true });
        el.addEventListener('error', () => {
          clearTimeout(fallbackTimer);
          // The lossless WAV failed to decode in this browser — recover with the
          // M4A rather than showing "corrupted" or skipping the track.
          if (audioRef.current && track.audioUrl && track.audioUrl !== track.wavUrl) {
            console.warn('[bb-player] WAV errored — falling back to M4A');
            decodeRetryRef.current = audioRef.current.src; // stop the global handler double-recovering
            setAudioError(null);
            loadAudio(track.audioUrl);
            audioRef.current.load();
            resumeAndPlay();
          }
        }, { once: true });
      }
    }
  }, [setupAudioContext, resumeAndPlay, resolveAudioUrl]);

  /** Past the end of the queue: a new queue of the same style (the injected
   *  auto-next provider), first track playing. False when there's no provider.
   *  See lib/autoplay-queue for why it no longer appends to the old queue. */
  const continuePastEnd = useCallback((currentQueue: MediaTrack[], currentIdx: number): boolean => {
    const providerFn = autoNextFnRef.current;
    if (!providerFn) return false;
    providerFn().then(tracks => {
      const next = buildAutoplayQueue(currentQueue, tracks.map(cleanTrack));
      if (next.length === 0) { setIsPlaying(false); return; }
      const basedOn = autoplayBasis(currentQueue[currentIdx], queueContextRef.current?.basedOn);
      autoplayFromRef.current = { queue: currentQueue, index: currentIdx, context: queueContextRef.current };
      setQueueContext({ kind: 'autoplay', basedOn });
      queueRef.current = next;
      setQueue(next);
      loadAndPlayIndex(0);
    }).catch(() => setIsPlaying(false));
    return true;
  }, [loadAndPlayIndex, setQueueContext]);

  const handleTrackEnd = useCallback(() => {
    const currentRepeat = repeatRef.current;
    const currentShuffle = shuffleRef.current;
    const currentQueue = queueRef.current;
    const currentIdx = queueIndexRef.current;

    if (currentRepeat === "one") {
      // Replay current track (from its saved in point, if any)
      if (audioRef.current) {
        audioRef.current.currentTime = trackBoundsRef.current.in || 0;
        resumeAndPlay();
      }
      return;
    }

    // Determine next index
    let nextIndex: number;
    if (currentShuffle) {
      if (currentQueue.length <= 1) {
        if (currentRepeat === "all") nextIndex = 0;
        else { setIsPlaying(false); return; }
      } else {
        nextIndex = Math.floor(Math.random() * currentQueue.length);
        while (nextIndex === currentIdx && currentQueue.length > 1) {
          nextIndex = Math.floor(Math.random() * currentQueue.length);
        }
      }
    } else {
      nextIndex = currentIdx + 1;
      if (nextIndex >= currentQueue.length) {
        if (currentRepeat === "all") {
          nextIndex = 0;
        } else {
          // Try to fetch same-genre tracks via the injected auto-next provider
          if (continuePastEnd(currentQueue, currentIdx)) return;
          setIsPlaying(false);
          return;
        }
      }
    }

    loadAndPlayIndex(nextIndex);
  }, [loadAndPlayIndex, resumeAndPlay, continuePastEnd]);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let lastSavedTime = -1;
    const onTimeUpdate = () => {
      const t = audio.currentTime;
      // Saved OUT marker → treat as end of track (advance queue / repeat).
      const outP = trackBoundsRef.current.out;
      if (outP > 0 && t >= outP && !audio.paused) {
        handleTrackEnd();
        return;
      }
      setCurrentTime(t);
      // Persist position every 5 s so refresh restores roughly where we were
      if (Math.abs(t - lastSavedTime) >= 5) {
        lastSavedTime = t;
        try {
          const raw = localStorage.getItem(PLAYER_STORAGE_KEY);
          const existing = raw ? JSON.parse(raw) : {};
          localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify({ ...existing, currentTime: t }));
        } catch { /* ignore */ }
      }
    };
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onEnded = () => handleTrackEnd();
    const onPlay = () => {
      setIsPlaying(true);
      setIsVisualizerActive(true);
      // Second-chance AudioContext setup — fires while we're in a user-gesture
      // stack, so the context is allowed to start immediately (Chrome policy).
      setupAudioContext();
      // Resume the AudioContext immediately from the audio element's own play event.
      // This is the most reliable place to do it: we're inside the browser's
      // user-activation window and don't need to await anything before calling resume().
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      // Track the play event — fire-and-forget
      const t = currentTrackRef.current;
      if (t) {
        trackMediaEvent({
          type: 'play',
          entityType: 'track',
          entityId: t.id || '',
          entityTitle: t.title,
          entityArtist: t.artist,
          entityGenre: t.genre,
        });
      }
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => { setIsLoading(false); consecutiveSkipsRef.current = 0; };
    const onError = (e: Event) => {
      console.error("Audio playback error:", e);
      setIsLoading(false);
      setIsPlaying(false);
      // Provide a human-readable error message based on MediaError code
      const mediaErr = (e.target as HTMLAudioElement)?.error;
      if (mediaErr) {
        const src = (e.target as HTMLAudioElement).src || "";
        const ext = src.split("?")[0].split(".").pop()?.toUpperCase() ?? "";
        if (mediaErr.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          setAudioError(
            ext && ["AIF", "AIFF"].includes(ext)
              ? `${ext} format cannot be played in this browser. Convert this file to M4A in the Media Vault.`
              : "This audio format is not supported in your browser."
          );
          // Auto-skip: if we're in a queue, move to the next track after a short
          // delay so Play All keeps going even when some tracks are AIF/unsupported.
          // Track consecutive skips to stop if ALL tracks in the queue are unplayable.
          consecutiveSkipsRef.current++;
          setTimeout(() => {
            const q = queueRef.current;
            const idx = queueIndexRef.current;
            if (q.length > 1 && consecutiveSkipsRef.current < q.length) {
              const nextIdx = (idx + 1) % q.length;
              if (nextIdx !== idx) {
                setAudioError(null);
                loadAndPlayIndex(nextIdx);
              }
            } else if (consecutiveSkipsRef.current >= q.length) {
              // Every track in the queue failed — stop and show clear message
              setAudioError("None of the tracks in this queue can be played. AIF/AIFF files need to be converted to M4A first in the Media Vault.");
              consecutiveSkipsRef.current = 0;
            }
          }, 1500);
        } else if (mediaErr.code === MediaError.MEDIA_ERR_NETWORK || mediaErr.code === MediaError.MEDIA_ERR_DECODE) {
          // Transient mid-stream failures masquerade as fatal here — a network
          // burp or a one-off decoder stall throws DECODE on files that are
          // perfectly healthy (Gordon: songs stopping + a 'corrupt' error on a
          // verified-good M4A). Try ONE in-place recovery: reload the source,
          // seek back to where we were, resume. Only surface the error if the
          // retry fails too.
          const el = e.target as HTMLAudioElement;
          const resumeAt = el.currentTime || 0;
          if (decodeRetryRef.current !== el.src) {
            decodeRetryRef.current = el.src;
            console.warn("Transient audio error — attempting in-place recovery at", resumeAt);
            setAudioError(null);
            setIsLoading(true);
            try {
              el.load();
              const onReady = () => {
                el.removeEventListener("canplay", onReady);
                try { el.currentTime = resumeAt; } catch { /* ignore */ }
                el.play().catch(() => { /* user gesture may be needed; bar shows play */ });
              };
              el.addEventListener("canplay", onReady, { once: true });
              return;
            } catch { /* fall through to the error message */ }
          }
          setAudioError(mediaErr.code === MediaError.MEDIA_ERR_NETWORK
            ? "Network error — couldn't load audio. Check your connection and try again."
            : "Audio file appears to be corrupted or in an unsupported format.");
        } else {
          setAudioError("Playback error — please try again.");
        }
      } else {
        setAudioError("Playback error — please try again.");
      }
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("error", onError);
    };
  }, [handleTrackEnd, loadAndPlayIndex]);

  // Sync volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Actions
  // A YouTube track plays in the player's iframe, not the <audio>: play/pause
  // set the state and the player relays it to the iframe (media-player.tsx).
  const isYtTrack = (tr: MediaTrack | null) => !!tr && (!!tr.youtubeId || tr.mediaType === "youtube");
  const play = useCallback(() => {
    if (isYtTrack(currentTrackRef.current)) { setIsPlaying(true); return; }
    setupAudioContext();
    resumeAndPlay();
  }, [setupAudioContext, resumeAndPlay]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    if (isYtTrack(currentTrackRef.current)) setIsPlaying(false);
  }, []);

  // One sound at a time: a video (or any other audio) starting with sound
  // pauses the player, and the player starting pauses them (lib/media-focus).
  // A YouTube track has no <audio> to pause — its iframe gets paused by the
  // focus manager, so mark it paused here too.
  useEffect(() => {
    installMediaFocus(() => audioRef.current);
    return onMediaFocus((source) => {
      if (source === "bb-player") return;
      audioRef.current?.pause();
      const tr = currentTrackRef.current;
      if (tr && (tr.youtubeId || tr.mediaType === "youtube")) setIsPlaying(false);
    });
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const setVolume = useCallback((vol: number) => {
    setVolumeState(Math.max(0, Math.min(1, vol)));
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const playTrack = useCallback((raw: MediaTrack) => {
    const track = cleanTrack(raw);
    const newQueue = [track];
    resetQueueContext();
    setQueue(newQueue);
    queueRef.current = newQueue;
    setQueueIndex(0);
    queueIndexRef.current = 0;
    setCurrentTrack(track);
    setIsActive(true);

    if (track.youtubeId || track.mediaType === "youtube") {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute('src'); }
      setIsPlaying(true);
      return;
    }

    const resolvedUrl2 = resolveAudioUrl(track);
    if (audioRef.current && resolvedUrl2) {
      loadAudio(resolvedUrl2);
      audioRef.current.load();
      setupAudioContext();
      resumeAndPlay();
    }
  }, [setupAudioContext, resumeAndPlay, resolveAudioUrl, resetQueueContext]);

  const playTracks = useCallback((raw: MediaTrack[], startIndex = 0) => {
    if (raw.length === 0) return;
    const tracks = raw.map(cleanTrack);
    const track = tracks[startIndex];
    resetQueueContext();
    setQueue(tracks);
    queueRef.current = tracks;
    setQueueIndex(startIndex);
    queueIndexRef.current = startIndex;
    setCurrentTrack(track);
    setIsActive(true);

    if (track.youtubeId || track.mediaType === "youtube") {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute('src'); }
      setIsPlaying(true);
      return;
    }

    const resolvedUrl3 = resolveAudioUrl(track);
    if (audioRef.current && resolvedUrl3) {
      loadAudio(resolvedUrl3);
      audioRef.current.load();
      setupAudioContext();
      resumeAndPlay();
    }
  }, [setupAudioContext, resumeAndPlay, resolveAudioUrl, resetQueueContext]);

  // Same as playTracks minus the play — no user gesture is needed, and none
  // is available on page load (browsers block autoplay with sound anyway).
  const cueTracks = useCallback((raw: MediaTrack[], startIndex = 0) => {
    const tracks = raw.map(cleanTrack);
    const track = tracks[startIndex];
    if (!track) return;
    resetQueueContext();
    setQueue(tracks);
    queueRef.current = tracks;
    setQueueIndex(startIndex);
    queueIndexRef.current = startIndex;
    setCurrentTrack(track);
    setIsActive(true);
    if (track.youtubeId || track.mediaType === "youtube") return;
    const resolved = resolveAudioUrl(track);
    if (audioRef.current && resolved) {
      loadAudio(resolved);
      audioRef.current.load();
    }
  }, [resolveAudioUrl, loadAudio, resetQueueContext]);

  const addToQueue = useCallback((track: MediaTrack) => {
    setQueue(prev => [...prev, cleanTrack(track)]);
  }, []);

  const removeFromQueue = useCallback((index: number) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
    if (index < queueIndex) {
      setQueueIndex(prev => prev - 1);
    }
  }, [queueIndex]);

  const clearQueue = useCallback(() => {
    resetQueueContext();
    setQueue([]);
    setQueueIndex(0);
    setCurrentTrack(null);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
    }
  }, [resetQueueContext]);

  const skipNext = useCallback(() => {
    const q = queueRef.current;
    const idx = queueIndexRef.current;
    if (q.length === 0) return;
    let nextIndex: number;
    if (shuffleRef.current) {
      nextIndex = Math.floor(Math.random() * q.length);
      while (nextIndex === idx && q.length > 1) {
        nextIndex = Math.floor(Math.random() * q.length);
      }
    } else {
      nextIndex = idx + 1;
      // At the end, Next goes on to more music — it used to wrap round, so a
      // one-track queue restarted the same song (Gordon 2026-10-31: "I said
      // next track many times and it plays the same track from the top").
      if (nextIndex >= q.length) {
        if (repeatRef.current !== "all" && continuePastEnd(q, idx)) return;
        nextIndex = 0;
      }
    }
    loadAndPlayIndex(nextIndex);
  }, [loadAndPlayIndex, continuePastEnd]);

  const skipPrev = useCallback(() => {
    const q = queueRef.current;
    const idx = queueIndexRef.current;
    if (q.length === 0) return;
    // If > 3 seconds in, restart current track
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    // Previous on the first autoplay track goes back to the queue it continued
    // from (e.g. the album that just ended), not round to the autoplay's end.
    const from = autoplayFromRef.current;
    if (!shuffleRef.current && idx === 0 && from && queueContextRef.current?.kind === 'autoplay') {
      autoplayFromRef.current = null;
      setQueueContext(from.context);
      queueRef.current = from.queue;
      setQueue(from.queue);
      loadAndPlayIndex(from.index);
      return;
    }
    let prevIndex: number;
    if (shuffleRef.current) {
      prevIndex = Math.floor(Math.random() * q.length);
    } else {
      prevIndex = idx - 1;
      if (prevIndex < 0) prevIndex = q.length - 1;
    }
    loadAndPlayIndex(prevIndex);
  }, [loadAndPlayIndex, setQueueContext]);

  // ── Media Session: OS/lock-screen media controls AND keeps playback alive
  // when the screen sleeps. Browsers keep MediaSession-registered audio playing
  // in the background instead of pausing it — without this, mobile/PWA audio
  // stops when the screen goes black. ──
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    if (currentTrack) {
      const art = currentTrack.imageUrl || currentTrack.images?.[0] || "";
      try {
        ms.metadata = new window.MediaMetadata({
          title: currentTrack.title || "Unknown Track",
          artist: currentTrack.artist || "Breakin' Bread",
          album: (currentTrack as any).album || "",
          artwork: art
            ? [
                { src: art, sizes: "512x512", type: "image/jpeg" },
                { src: art, sizes: "1200x1200", type: "image/jpeg" },
              ]
            : [],
        });
      } catch { /* MediaMetadata unsupported */ }
    }
    try { ms.playbackState = isPlaying ? "playing" : "paused"; } catch { /* older browsers */ }
    const setH = (action: MediaSessionAction, handler: (() => void) | null) => {
      try { ms.setActionHandler(action, handler); } catch { /* unsupported action */ }
    };
    setH("play", () => play());
    setH("pause", () => pause());
    setH("previoustrack", () => skipPrev());
    setH("nexttrack", () => skipNext());
  }, [currentTrack, isPlaying, play, pause, skipNext, skipPrev]);

  const toggleShuffle = useCallback(() => {
    setShuffle(prev => {
      const next = !prev;
      // When turning shuffle ON, turn repeat OFF (they toggle each other)
      if (next) setRepeat("off");
      return next;
    });
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeat(prev => {
      let next: RepeatMode;
      if (prev === "off") next = "all";
      else if (prev === "all") next = "one";
      else next = "off";
      // When turning repeat ON, turn shuffle OFF (they toggle each other)
      if (next !== "off") setShuffle(false);
      return next;
    });
  }, []);

  const setActive = useCallback((active: boolean) => {
    setIsActive(active);
    if (!active) {
      setIsExpanded(false);
    }
  }, []);

  const setExpanded = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  const formatTime = useCallback((seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  }, []);

  /**
   * Connect an external HTMLAudioElement (e.g. mastering preview audio) to
   * the Web Audio analyser chain so VU meters light up for that source.
   * Returns a cleanup function that disconnects and restores state.
   */

  const activateExternalVisualizer = useCallback(() => {
    setIsExternalYT(true);
    setIsVisualizerActive(true);
  }, []);

  const deactivateExternalVisualizer = useCallback(() => {
    setIsExternalYT(false);
    // Only silence the visualizer if the main player isn't also playing
    setIsVisualizerActive(prev => {
      // Let the main player's own play/pause effects control isVisualizerActive
      return false;
    });
  }, []);

  // DJ mixer calls this to announce what's playing on the master deck.
  // active=true: VU + now-playing indicator; active=false: clears both.
  const setDJMode = useCallback((active: boolean, info?: { title: string; artist?: string } | null) => {
    setIsDJMode(active);
    setDjTrackInfo(active ? (info ?? null) : null);
    if (active) setIsVisualizerActive(true);
  }, []);

  const connectExternalAudio = useCallback((el: HTMLAudioElement) => {
    if (typeof window === "undefined") return () => {};

    // Register for playback coordination FIRST, outside the Web Audio try/catch
    // below — this must keep working even when analyser wiring fails or throws
    // (e.g. createMediaElementSource can only be called once per <audio>
    // element ever, so a reused element throws on its 2nd+ connect here).
    // Starting this external element pauses the footer player, and the footer
    // starting pauses every registered external element — see `play`/
    // `resumeAndPlay` below. Without this, the footer and e.g. the Media
    // Vault trim editor's preview player could both be "playing" at once with
    // nothing in the app coordinating which one the user actually hears.
    externalAudioElsRef.current.add(el);
    const onExternalPlay = () => { audioRef.current?.pause(); };
    el.addEventListener('play', onExternalPlay);
    const unregister = () => {
      externalAudioElsRef.current.delete(el);
      el.removeEventListener('play', onExternalPlay);
    };

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return unregister;

      // Reuse the existing AudioContext or create a fresh one
      const ctx = audioCtxRef.current ?? new AudioContextClass();
      audioCtxRef.current = ctx;

      // Resume if suspended (required by browsers after user gesture)
      if (ctx.state === "suspended") ctx.resume().catch(() => {});

      // Build a fresh analyser pair for the external source
      const nodeL = ctx.createAnalyser();
      const nodeR = ctx.createAnalyser();
      nodeL.fftSize = 2048; nodeL.smoothingTimeConstant = 0.85;
      nodeR.fftSize = 2048; nodeR.smoothingTimeConstant = 0.85;

      const splitter = ctx.createChannelSplitter(2);
      const merger   = ctx.createChannelMerger(2);
      const source   = ctx.createMediaElementSource(el);

      source.connect(splitter);
      splitter.connect(nodeL, 0);
      splitter.connect(nodeR, 1);
      nodeL.connect(merger, 0, 0);
      nodeR.connect(merger, 0, 1);
      merger.connect(ctx.destination);

      // Save current analysers so we can restore them when external source stops
      const prevAnalyserL = analyserLRef.current;
      const prevAnalyserR = analyserRRef.current;

      // Expose new analysers to VU meters
      setAnalyserL(nodeL);
      setAnalyserR(nodeR);
      setIsVisualizerActive(true);

      // Listen for play/pause/end on the external element to sync visualizer state
      const onPlay  = () => setIsVisualizerActive(true);
      const onPause = () => setIsVisualizerActive(false);
      const onEnded = () => setIsVisualizerActive(false);
      el.addEventListener('play',  onPlay);
      el.addEventListener('pause', onPause);
      el.addEventListener('ended', onEnded);

      return () => {
        unregister();
        el.removeEventListener('play',  onPlay);
        el.removeEventListener('pause', onPause);
        el.removeEventListener('ended', onEnded);
        try { source.disconnect(); } catch {}
        try { nodeL.disconnect(); } catch {}
        try { nodeR.disconnect(); } catch {}
        setIsVisualizerActive(false);
        // Restore the main player's analysers so VU meters work again after
        // the external source finishes (they're still live in the audio graph).
        setAnalyserL(prevAnalyserL);
        setAnalyserR(prevAnalyserR);
      };
    } catch (e) {
      console.warn("[bb-player] connectExternalAudio failed:", e);
      return unregister;
    }
  }, []);

  /**
   * Connect a live MediaStream (mic / screen audio) to the VU analyser chain.
   * The previous analyserL/R are restored when the returned cleanup function runs.
   */
  const connectMicStream = useCallback((stream: MediaStream): (() => void) => {
    if (typeof window === "undefined") return () => {};
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return () => {};

      const ctx = audioCtxRef.current ?? new AudioContextClass();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});

      const nodeL = ctx.createAnalyser();
      const nodeR = ctx.createAnalyser();
      nodeL.fftSize = 2048; nodeL.smoothingTimeConstant = 0.8;
      nodeR.fftSize = 2048; nodeR.smoothingTimeConstant = 0.8;

      // MediaStreamSourceNode → splitter → analyserL/R → merger → destination
      const source   = ctx.createMediaStreamSource(stream);
      const splitter = ctx.createChannelSplitter(Math.min(stream.getAudioTracks().length > 0 ? 2 : 1, 2));
      const merger   = ctx.createChannelMerger(2);

      source.connect(splitter);
      // Mono mic: both L and R come from channel 0
      const channelCount = (source.channelCount ?? 1);
      splitter.connect(nodeL, 0);
      splitter.connect(nodeR, channelCount > 1 ? 1 : 0);
      nodeL.connect(merger, 0, 0);
      nodeR.connect(merger, 0, 1);
      // Note: mic stream does NOT connect to ctx.destination — we only visualise it

      const prevL = analyserLRef.current;
      const prevR = analyserRRef.current;

      setAnalyserL(nodeL);
      setAnalyserR(nodeR);
      setIsVisualizerActive(true);

      return () => {
        try { source.disconnect(); } catch {}
        try { nodeL.disconnect(); } catch {}
        try { nodeR.disconnect(); } catch {}
        setIsVisualizerActive(false);
        setAnalyserL(prevL);
        setAnalyserR(prevR);
      };
    } catch (e) {
      console.warn("[bb-player] connectMicStream failed:", e);
      return () => {};
    }
  }, []);

  // ── navigator.mediaSession — OS "now playing" integration ────────────────
  // Updates the OS / browser media session metadata (lock screen, notification
  // centre, AirPods controls, keyboard media keys) whenever the track or
  // playback state changes.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!currentTrack) {
      navigator.mediaSession.metadata = null;
      return;
    }
    const artworkUrl = currentTrack.imageUrl || currentTrack.images?.[0] || "";
    const artwork: MediaImage[] = artworkUrl
      ? (["96x96", "128x128", "192x192", "256x256", "512x512"] as const).map(sz => ({
          src: artworkUrl,
          sizes: sz,
          type: "image/jpeg",
        }))
      : [];
    navigator.mediaSession.metadata = new MediaMetadata({
      title:  currentTrack.title  || "Unknown Track",
      artist: currentTrack.artist || "Breakin’ Bread",
      album:  currentTrack.album  || "BB Radio",
      artwork,
    });
  }, [currentTrack]);

  // Sync playback state to OS media session
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  // Register hardware media key / remote control handlers
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const handlers: Partial<Record<MediaSessionAction, MediaSessionActionHandler>> = {
      play:          () => play(),
      pause:         () => pause(),
      nexttrack:     () => skipNext(),
      previoustrack: () => skipPrev(),
      seekto: (details) => {
        if (details.seekTime !== undefined) seek(details.seekTime);
      },
      seekbackward: (details) => {
        if (audioRef.current) seek(Math.max(0, audioRef.current.currentTime - (details.seekOffset ?? 10)));
      },
      seekforward: (details) => {
        if (audioRef.current) seek(Math.min(duration, audioRef.current.currentTime + (details.seekOffset ?? 10)));
      },
    };
    for (const [action, handler] of Object.entries(handlers) as [MediaSessionAction, MediaSessionActionHandler][]) {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch { /* action not supported by this browser */ }
    }
    return () => {
      for (const action of Object.keys(handlers) as MediaSessionAction[]) {
        try { navigator.mediaSession.setActionHandler(action, null); } catch { /* non-fatal */ }
      }
    };
  }, [play, pause, skipNext, skipPrev, seek, duration]);

  const value: MediaPlayerContextType = {
    // State
    currentTrack,
    queue,
    queueIndex,
    queueContext,
    isPlaying,
    isLoading,
    audioError,
    currentTime,
    duration,
    volume,
    isMuted,
    shuffle,
    repeat,
    isActive,
    isExpanded,
    analyserL,
    analyserR,
    isVisualizerActive,
    isExternalYT,
    isDJMode,
    djTrackInfo,
    streamQuality,
    // Actions
    play,
    pause,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    playTrack,
    playTracks,
    cueTracks,
    addToQueue,
    removeFromQueue,
    clearQueue,
    skipNext,
    skipPrev,
    toggleShuffle,
    cycleRepeat,
    setStreamQuality: handleSetStreamQuality,
    setActive,
    setExpanded,
    formatTime,
    setAutoNextProvider,
    connectExternalAudio,
    connectMicStream,
    activateExternalVisualizer,
    deactivateExternalVisualizer,
    setDJMode,
  };

  return (
    <MediaPlayerContext.Provider value={value}>
      {children}
    </MediaPlayerContext.Provider>
  );
}
